import classNames from 'classnames';
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {Query, Mutation} from 'react-apollo';
import gql from 'graphql-tag';
import TestExposureOnce from '../../abacus/test-exposure-once';
import TripboardCard from './tripboard-card';
import EmptyTripboardLabel from './empty-tripboard-label';
import ImprovedEmptyState from '../improved-empty-state/improved-empty-state';
import OnboardingCarousel from '../../components/onboarding/onboarding-carousel';
import {CreateTripboardModal, CreateTripboardCard} from '../create-tripboard-controls';
import CreateTripboardActions from '../create-tripboard-controls/actions';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import edap from '../edap';
import PropTypes from 'prop-types';
import {getIsMobile} from '../../selectors/system';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import './tripboards.less';
import withAbacusTestValuesAsProps from '../../abacus/with-abacus-test-values-as-props';
import withAuth from '../../components/auth/with-auth';
// Abacus: HA_PULSE_SKELETON_ABC
import {WATERFALL_AB, HA_PULSE_CREATE_BOARD, HA_PULSE_IMPROVED_EMPTY_STATE} from '../../abacus/experiments';
export const testExposureCreateTripboard = new TestExposureOnce(HA_PULSE_CREATE_BOARD);
export const fireImprovedEmptyStateExposure = new TestExposureOnce(HA_PULSE_IMPROVED_EMPTY_STATE);

const getImageUrl = (image) => {
    if (!image || !image.c6_uri) {
        return '';
    }
    return image.c6_uri;
};

export const PAGE_SIZE = 200;
export const LISTING_PAGE_SIZE = 3;

export const PAGINATED_TRIPBOARDS_QUERY = gql`
    query paginatedTripBoards($hav: String!, $pageSize: Int!, $listingPageSize: Int) {
        paginatedTripBoards(hav: $hav, pageSize: $pageSize) {
            tripBoards {
                uuid
                name
                listingRefs
                listings(pageSize: $listingPageSize) {
                    images {
                        c6_uri
                        height
                        width
                    }
                }
                user {
                    role {
                        type
                    }
                }
            }
        }
    }
`;

export const TRIPBOARDS_DELETE = gql`
    mutation deleteTripBoard($hav: String!, $tripBoardUuid: String!) {
        deleteTripBoard(hav: $hav, tripBoardUuid: $tripBoardUuid)
    }
`;
export const queryParams = (hav) => {
    return {
        hav,
        pageSize: PAGE_SIZE,
        listingPageSize: LISTING_PAGE_SIZE
    };
};

export class Tripboards extends Component {
    static propTypes = {
        listings: PropTypes.arrayOf(PropTypes.shape),
        translations: PropTypes.objectOf(PropTypes.string).isRequired
    };

    static defaultProps = {
        listings: [],
        markPAR: () => {},
        waterfallTestValue: 0,
        smallerImageTestValue: 0
    };

    componentDidMount = () => {
        const {loading} = this.props;
        if (!loading) {
            this.loadEvents();
        }
    };

    componentDidUpdate() {
        if (!this.props.loading) {
            this.loadEvents();
        }
    }

    loadEvents() {
        this.par(this.props);
        const {
            showCreateTripboardButton,
            previews,
            createTripboardTestValue,
            improvedEmptyStateTestValue,
            isLoggedIn,
            isMobile
        } = this.props;
        edap.summariesLoad(previews.length);
        const hasTripboards = !!previews.length;
        const showImprovedEmptyState = this.showImprovedEmptyState(isLoggedIn, improvedEmptyStateTestValue);
        let showCreateButton =
            hasTripboards && (createTripboardTestValue === 1 || (createTripboardTestValue === 2 && isMobile));

        if (!hasTripboards && !this.showOnboarding() && !isLoggedIn) {
            showCreateButton = showImprovedEmptyState;
            fireImprovedEmptyStateExposure.trigger(HA_PULSE_IMPROVED_EMPTY_STATE);
        } else if (hasTripboards) {
            testExposureCreateTripboard.trigger(HA_PULSE_CREATE_BOARD);
        }

        showCreateTripboardButton(showCreateButton);
    }

    cardProps = (board, index, boards) => {
        const {onDelete, translations, waterfallTestValue} = this.props;
        const thumbnails = board.listings.map((listing) => listing.images && listing.images[0]);
        const {name, uuid: boardId, listingRefs} = board;
        const isWaterFallTestOn = waterfallTestValue > 0;
        return {
            count: listingRefs ? listingRefs.length : 0,
            boardId,
            image1: getImageUrl(thumbnails[0]),
            image2: getImageUrl(thumbnails[1]),
            image3: getImageUrl(thumbnails[2]),
            name,
            onDelete,
            role: board.role,
            translations,
            index,
            total: boards.length,
            isWaterFallTestOn, // Abacus: waterfall
            isLazyLoad: isWaterFallTestOn && index > 2 // Abacus: waterfall
        };
    };

    isEmpty = () => {
        const {loading, previews} = this.props;
        /** @todo error handling: failing silently in the UI */
        return !loading && !previews.length;
    };

    showImprovedEmptyState = (isLoggedIn, improvedEmptyStateTestValue) =>
        improvedEmptyStateTestValue > 0 && !isLoggedIn;

    showOnboarding = () => {
        /* we want to show carousel when:
           1. no tripboards AND user has NOT seen landing page
           2. tripboard BUT they have not seen the details page for the tripboard
           3. User completed onboarding but have not completed new tripboard creation */
        const {tripboardCreationNotComplete, tripboardCreationStarted, onboardingCompleted} = this.props;

        return !onboardingCompleted && (this.isEmpty() || (tripboardCreationNotComplete && tripboardCreationStarted));
    };

    par = (props = {}) => {
        const {loading} = props;
        // PAR definition: When tripboards are fully loaded (clickable).
        if (!loading) {
            this.props.markPAR();
        }
    };

    handleBoardDelete = (mutation, hav) => (boardId) => {
        mutation({
            variables: {
                hav,
                tripBoardUuid: boardId
            }
        });
    };

    updateCache = (boardId, hav) => (cache) => {
        const data =
            cache.readQuery({
                query: PAGINATED_TRIPBOARDS_QUERY,
                variables: queryParams(hav, PAGE_SIZE, LISTING_PAGE_SIZE)
            }) || {};
        const {tripBoards = []} = data.paginatedTripBoards || {};
        tripBoards.splice(tripBoards.findIndex((tripboard) => tripboard.uuid === boardId), 1);
        data.paginatedTripBoards.tripBoards = tripBoards;
        cache.writeQuery({
            query: PAGINATED_TRIPBOARDS_QUERY,
            variables: queryParams(hav, PAGE_SIZE, LISTING_PAGE_SIZE),
            data
        });
    };

    render() {
        const {
            loading,
            previews,
            hav,
            createTripboardModalOpen,
            handleCreateModalClose,
            createTripboardTestValue,
            isMobile,
            improvedEmptyStateTestValue,
            isLoggedIn
        } = this.props;
        const isEmpty = this.isEmpty();
        const showOnboarding = this.showOnboarding();
        const showTripboadCard = !isMobile && !isEmpty && createTripboardTestValue === 2;
        const showImprovedEmptyState = this.showImprovedEmptyState(isLoggedIn, improvedEmptyStateTestValue);
        const containerClass = classNames({
            'tripboard-summary-container': true,
            'tripboard-summary-empty': isEmpty,
            'is-empty': isEmpty
        });

        if (loading) {
            return (
                <div className={`${containerClass} is-skeleton`} data-wdio="tripboard-summary__container">
                    <TripboardCard
                        onDelete={() => {}} // eslint-disable-line
                        translations={this.props.translations}
                        isLoading
                    />
                </div>
            );
        }

        if (showOnboarding) {
            return <OnboardingCarousel tripboards={previews} />;
        }

        return (
            <div className={containerClass}>
                <LoadingOverlay loading={loading} />
                {isEmpty &&
                !showOnboarding &&
                !showImprovedEmptyState && <EmptyTripboardLabel translations={this.props.translations} large />}
                {isEmpty && !showOnboarding && showImprovedEmptyState && <ImprovedEmptyState />}
                {showTripboadCard && <CreateTripboardCard />}
                {!isEmpty &&
                    previews.map(this.cardProps).map((props) => (
                        <Mutation
                            key={props.boardId}
                            mutation={TRIPBOARDS_DELETE}
                            update={this.updateCache(props.boardId, hav)}
                        >
                            {(deleteTripBoard) => (
                                <TripboardCard {...props} onDelete={this.handleBoardDelete(deleteTripBoard, hav)} />
                            )}
                        </Mutation>
                    ))}
                <CreateTripboardModal handleModalClose={handleCreateModalClose} isOpen={createTripboardModalOpen} />
            </div>
        );
    }
}

export const TripboardsProvider = (ownProps) => (
    <Query
        query={PAGINATED_TRIPBOARDS_QUERY}
        errorPolicy={'all'}
        skip={!ownProps.hav}
        ssr={false}
        variables={queryParams(ownProps.hav, PAGE_SIZE, LISTING_PAGE_SIZE)}
    >
        {({data, loading}) => {
            const previews = (data && data.paginatedTripBoards && data.paginatedTripBoards.tripBoards) || [];

            return <Tripboards {...{...ownProps, previews, loading}} />;
        }}
    </Query>
);

const mapStateToProps = (state) => ({
    tripboardCreationNotComplete: state && state.onboarding && state.onboarding.tripboardCreationNotComplete,
    tripboardCreationStarted: state && state.onboarding && state.onboarding.tripboardCreationStarted,
    onboardingCompleted: state && state.onboarding && state.onboarding.onboardingCompleted,
    createTripboardModalOpen:
        state && state.tripboard && state.tripboard.createControls && state.tripboard.createControls.modalOpen,
    isMobile: getIsMobile(state)
});

const mapDispatchToProps = {
    showCreateTripboardButton: CreateTripboardActions.showButton,
    handleCreateModalClose: CreateTripboardActions.closeModal
};

// export wired component as default
export default compose(
    withAbacusTestValuesAsProps({
        [WATERFALL_AB]: 'waterfallTestValue',
        [HA_PULSE_CREATE_BOARD]: 'createTripboardTestValue',
        [HA_PULSE_IMPROVED_EMPTY_STATE]: 'improvedEmptyStateTestValue'
    }),
    connect(mapStateToProps, mapDispatchToProps),
    determineIfMobile,
    withAuth
)(TripboardsProvider);



// WEBPACK FOOTER //
// ./src/universal/tripboard/components/tripboards.js